import { Id } from '@/infra/id'
import { observable, computed, action, runInAction } from 'mobx'
import { fromStream, toStream } from '@/utils/mobx-util'
import { randomUUID } from '@/utils/uuid-util'
import {
  switchMap,
  distinctUntilChanged,
  map,
  filter,
  withLatestFrom,
  share,
  catchError,
} from 'rxjs/operators'
import { Subject, of, defer, merge, type Observable } from 'rxjs'
import { type Job } from '@/features/jobs/Job'
import { type Document } from '@/features/documents/Document'
import {
  type DocumentStore,
  type UploadDocumentProgress,
} from '@/features/documents/DocumentStore'
import { withAbortSignal, shareBehavior, unpair } from '@/utils/rx-util'
import { ABORTED, resolveOnAbort } from '@/utils/abort-util'
import {
  type DocumentPickerViewModel,
  type DocumentPickerViewModelOptions,
} from '@/features/documents/DocumentPicker/DocumentPickerViewModel'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { RemoveDocumentRequestDialogViewModel } from './RemoveDocumentRequestDialog/RemoveDocumentRequestDialogViewModel'

/**
 * Creates a document picker.
 */
export type InternalCreateDocumentPickerViewModelFn = (
  options: DocumentPickerViewModelOptions,
) => DocumentPickerViewModel

/**
 * Document requests view model, for requesting documents for a job.
 */
export class DocumentRequestsViewModel {
  @observable.ref
  private accessor _documentRequests: RequestedDocumentViewModel[] = []

  constructor(
    private readonly _job: Job,
    private readonly _documentStore: DocumentStore,
    private readonly _createDocumentPickerViewModel: InternalCreateDocumentPickerViewModelFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Emits the descriptors whenever a document is provided outside
   * reconciliation.
   */
  readonly changes$: Observable<RequestedDocumentDescriptor[]> = toStream(
    () => this._documentRequests,
    true,
  ).pipe(
    switchMap((requests) => merge(...requests.map((r) => r.changes$))),
    map(() => this.documentRequestDescriptors),
  )

  /**
   * Document requests.
   */
  @computed.struct
  get documentRequests() {
    return this._documentRequests.slice()
  }

  /**
   * Gets the document request descriptors with the selected document.
   */
  @computed.struct
  get documentRequestDescriptors() {
    return this._documentRequests.map((r) => r.toDescriptor())
  }

  /**
   * Uploads a supplemental document not tied to a particular predefined option.
   */
  @action.bound
  uploadSupplemental(file: File) {
    const requestedDoc = this._createRequest({
      id: 'SUPPLEMENTAL_DOC_' + randomUUID().replaceAll(/-/g, ''),
      title: file.name,
      supplemental: true,
    })
    this._documentRequests = [...this._documentRequests, requestedDoc]
    requestedDoc.newDocument(file)
  }

  /**
   * Uploads supplemental documents.
   */
  @action.bound
  uploadSupplementalFiles(files: File[]) {
    files.forEach(this.uploadSupplemental)
  }

  /**
   * Reconciles the document requests with the descriptors, keeping the
   * requests whose IDs are still described.
   */
  @action.bound
  reconcile(descriptors: RequestedDocumentDescriptor[]) {
    const existing = new Map(this._documentRequests.map((r) => [r.id, r]))
    const next = descriptors.map((descriptor) => {
      const request = existing.get(descriptor.id)
      if (!request) {
        return this._createRequest(descriptor)
      }

      existing.delete(descriptor.id)
      request.update(descriptor)
      return request
    })

    for (const removed of existing.values()) {
      removed.dispose()
    }

    this._documentRequests = next
  }

  private _createRequest(descriptor: RequestedDocumentDescriptorInput) {
    const request = new RequestedDocumentViewModel(
      descriptor.id,
      this._job,
      this._documentStore,
      this._createDocumentPickerViewModel,
      this._createAlertErrorHandler,
    )
    request.update(descriptor)
    return request
  }
}

/**
 * Describes a requested document.
 */
export interface RequestedDocumentDescriptor {
  /**
   * ID that uniquely identifies the descriptor in the list.
   */
  readonly id: string
  /**
   * The title of the document request (for example, Form W-2).
   */
  readonly title: string
  /**
   * Tag IDs to assign to new documents being uploaded through this descriptor.
   * Optional.
   */
  readonly documentTagIds?: string[]
  /**
   * The document ID if one is already selected.
   */
  readonly documentId?: Id | null
  /**
   * If the document is not tied to a particular answer
   */
  readonly supplemental: boolean
}

/**
 * The parts of a descriptor that can be applied to a request.
 */
type RequestedDocumentDescriptorInput = Pick<
  RequestedDocumentDescriptor,
  'id' | 'title' | 'supplemental'
> &
  Partial<RequestedDocumentDescriptor>

/**
 * A document being uploaded into the request.
 */
interface RequestedDocumentUpload {
  document: Document
  progress: UploadDocumentProgress
}

/**
 * View model for a requested document.
 */
export class RequestedDocumentViewModel {
  /**
   * Title to display to the user and use as the title for
   * any newly uploaded document.
   */
  @observable accessor title = 'Document'

  /**
   * IDs of document tags to assign to new docs.
   */
  @observable.shallow accessor documentTagIds: string[] = []

  /**
   * Whether this is a supplemental document and not tied to any input option.
   */
  @observable accessor supplemental = false

  /**
   * The upload whose progress is shown, if any.
   */
  @observable.ref
  private accessor _currentUpload: RequestedDocumentUpload | null = null

  /**
   * Action inputs for the processing stream.
   */
  private readonly _documentInput$ = new Subject<DocumentInput>()

  /**
   * Deduplicates the input so that we don't attempt to fetch the doc that is currently selected
   * in case we reconcile the same ID.
   */
  private readonly _dedupedInput$: Observable<DocumentInput> =
    this._documentInput$.pipe(
      // To do this, we need the currently selected document.
      withLatestFrom(defer(() => this.document$)),
      filter(([nextInput, currentDoc]) => {
        // If there's not a current document, then
        // it would not be considered a change
        if (!currentDoc) {
          return nextInput.type !== 'ClearDocument'
        }

        // Any upload would be a change.
        if (nextInput.type === 'UploadNewDocument') {
          return true
        }

        // Otherwise, if the ID of whatever the input represents
        // is the same as the current document, ignore.
        const nextInputKey = getInputKey(nextInput)
        return nextInputKey !== currentDoc.id
      }),
      unpair(),
      // Ignores consecutive inputs that have the same ID.
      distinctUntilChanged(areInputsEqual),
      share(),
    )

  /**
   * Observable that manages the ((up)loaded) document.
   *
   * The consumer provides the document request descriptors (and their
   * current values) reactively, so the state must reconcile safely.
   */
  private readonly _documentIO$ = this._dedupedInput$.pipe(
    // Ensures we don't have race conditions between
    // setting IDs -> async fetch, uploading, and setting the doc directly.
    // We return the pair of I/O so we can ignore sets that were caused by reconciliation.
    switchMap((input): Observable<[DocumentInput, Document | null]> => {
      switch (input.type) {
        case 'ClearDocument':
          return of([input, null])
        case 'SetDocument':
          return of([input, input.document])
        case 'SetID':
          return withAbortSignal((signal) =>
            this._fetch(input.documentId, signal),
          ).pipe(
            map((d): [DocumentInput, Document | null] => [
              input,
              d ?? /* istanbul ignore next */ null,
            ]),
            // A document that can't be fetched leaves only its ID provided.
            catchError(() => of<[DocumentInput, null]>([input, null])),
          )
        case 'UploadNewDocument':
          return withAbortSignal((signal) => {
            // Emits the document as soon as it exists, but only completes
            // once its contents are uploaded.
            const subject = new Subject<Document | null>()
            this._upload(input.file, subject.next.bind(subject), signal)
              .catch(() => subject.next(null))
              .finally(subject.complete.bind(subject))
            return subject.asObservable()
          }).pipe(map((d) => [input, d]))
      }
    }),
    share(),
  )

  /**
   * Observable that keeps the current document, `null` if none is selected.
   */
  readonly document$ = this._documentIO$.pipe(
    map(([, doc]) => doc),
    // Only keep a single subscriber, emit `null` as the initial value.
    shareBehavior<Document | null>(null),
  )

  /**
   * The document long ID. Even if we couldn't fetch the document, the ID is
   * still there, so we can use that to identify a provided document.
   * Reconciled answers may still carry short IDs from before the long-id
   * switch; the fetch endpoint resolves both.
   */
  readonly documentId$ = this._documentIO$.pipe(
    map(([input, doc]) => {
      if (doc) {
        return doc.id
      }

      switch (input.type) {
        case 'ClearDocument':
          return null
        case 'SetID':
          return input.documentId
        default:
          return null
      }
    }),
    shareBehavior<Id | null>(null),
  )

  /**
   * Observable that only emits the document when it was changed
   * outside reconciliation.
   */
  readonly changes$ = this._documentIO$.pipe(
    // Don't emit the fetched doc if we were reconciling.
    filter(([input]) => input.type !== 'SetID'),
    map(([, doc]) => doc),
    share(),
  )

  /**
   * MobX<>Rx maps
   */
  private readonly _$ = {
    document: fromStream(null, this.document$),
    documentId: fromStream(Id.none, this.documentId$),
  }

  /**
   * Document picker view model.
   */
  readonly documentPickerVm: DocumentPickerViewModel

  /**
   * Confirms removing the document from the slot.
   */
  readonly removeDialogVm = new RemoveDocumentRequestDialogViewModel()

  constructor(
    readonly id: string,
    private readonly _job: Job,
    private readonly _documentStore: DocumentStore,
    createDocumentPickerViewModel: InternalCreateDocumentPickerViewModelFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this.clear = this.clear.bind(this)
    this.remove = this.remove.bind(this)
    this.setDocument = this.setDocument.bind(this)
    this.newDocument = this.newDocument.bind(this)
    this.download = this.download.bind(this)
    this.documentPickerVm = createDocumentPickerViewModel({
      job: this._job,
      pick: {
        isPicked: (doc) => this.document === doc,
        onPick: this.setDocument,
      },
    })
  }

  /**
   * The currently selected document.
   */
  @computed
  get document(): Document | null {
    return this._$.document()
  }

  /**
   * ID of the currently selected document. If for some reason the fetch failed,
   * this will still be set while `document` will be null.
   */
  @computed
  get documentId(): Id | null {
    return this._$.documentId()
  }

  /**
   * How much of the selected document's contents have been uploaded, in
   * percent, or `null` when it isn't being uploaded.
   */
  @computed
  get uploadPercent(): number | null {
    const upload = this._currentUpload
    if (upload?.document !== this.document || !upload) {
      return null
    }

    const { uploaded, total } = upload.progress
    return total ? Math.ceil((uploaded / total) * 100) : 0
  }

  /**
   * Sets the document.
   * @param document
   */
  setDocument(document: Document) {
    this._documentInput$.next({ document, type: 'SetDocument' })
  }

  /**
   * Clears the selected document if one is selected.
   */
  clear() {
    this._documentInput$.next({ type: 'ClearDocument' })
  }

  /**
   * Clears the selected document once the user confirms.
   */
  async remove() {
    if (await this.removeDialogVm.prompt()) {
      this.clear()
    }
  }

  /**
   * Starts uploading a document.
   *
   * @param file
   */
  newDocument(file: File) {
    this._documentInput$.next({
      file,
      type: 'UploadNewDocument',
    })
  }

  /**
   * Creates a descriptor for this view model.
   */
  toDescriptor(): RequestedDocumentDescriptor {
    return {
      id: this.id,
      title: this.title,
      documentId: this.documentId,
      documentTagIds: this.documentTagIds.slice(),
      supplemental: this.supplemental,
    }
  }

  /**
   * Applies the descriptor, fetching its document when it changed.
   */
  @action.bound
  update({
    documentId,
    title,
    documentTagIds,
    supplemental,
  }: RequestedDocumentDescriptorInput) {
    this._documentInput$.next(
      documentId ? { type: 'SetID', documentId } : { type: 'ClearDocument' },
    )
    this.title = title
    this.supplemental = supplemental
    if (documentTagIds) {
      this.documentTagIds = documentTagIds
    }
  }

  async download() {
    if (this.document) {
      await this._documentStore
        .download({ document: this.document, preview: false })
        .catch(this._createAlertErrorHandler('Downloading failed'))
    }
  }

  /**
   * Disposes the underlying observables.
   */
  dispose() {
    this._documentInput$.complete()
  }

  /**
   * Called when we receive a new document ID.
   */
  private async _fetch(
    id: string,
    signal: AbortSignal,
  ): Promise<Document | null | undefined> {
    const result = await resolveOnAbort(
      this._documentStore.fetchDocument(Id(id), { history: true }),
      signal,
    )
    return result === ABORTED ? undefined : result
  }

  /**
   * Uploads the document.
   */
  private async _upload(
    file: File,
    onDocumentCreated: (document: Document) => void,
    signal: AbortSignal,
  ) {
    const upload = this._documentStore.upload(
      {
        file,
        workspaceId: Id.assert(this._job.workspace.id),
        jobId: Id.assert(this._job.id),
      },
      signal,
    )

    void upload.created
      .then(async (document) => {
        onDocumentCreated(document)
        await upload.progress$.forEach(
          action((progress) => {
            this._currentUpload = { document, progress }
          }),
        )
        runInAction(() => {
          if (this._currentUpload?.document === document) {
            this._currentUpload = null
          }
        })
      })
      // `upload.completed` reports the failure.
      .catch(() => undefined)

    await resolveOnAbort(upload.completed, signal)
  }
}

/**
 * Ways to provide a document.
 */
type DocumentInput = SetID | ClearDocument | SetDocument | UploadNewDocument

/**
 * Sets the document ID, will result in a fetch for the doc.
 * Used when reconciling. DO NOT USE OUTSIDE RECONCILING as it will
 * not emit a change.
 */
interface SetID {
  type: 'SetID'
  documentId: string
}

/**
 * Clears the currently selected document.
 */
interface ClearDocument {
  type: 'ClearDocument'
}

/**
 * Sets the document directly, no fetch needed.
 */
interface SetDocument {
  type: 'SetDocument'
  document: Document
}

/**
 * Starts uploading a new document
 */
interface UploadNewDocument {
  type: 'UploadNewDocument'
  file: File
}

/**
 * Whether inputs are equal, used for ignoring consecutive changes of the same input
 * if relevant.
 *
 * @param left
 * @param right
 */
function areInputsEqual(left: DocumentInput, right: DocumentInput) {
  // When either side is an "upload", we don't dedupe.
  if (left.type === 'UploadNewDocument' || right.type === 'UploadNewDocument') {
    return false
  }

  return getInputKey(left) === getInputKey(right)
}

/**
 * Normalizes input key.
 *
 * @param input
 */
function getInputKey(input: DocumentInput) {
  switch (input.type) {
    case 'ClearDocument':
      return null
    case 'SetDocument':
      return Id(input.document.id)
    case 'SetID':
      return Id(input.documentId)
  }
}
